// frontend/src/utils/gridLocale.js
//
// The words AG Grid draws itself — the pagination bar, the Columns panel, the
// right-click menu, the dropdown editor's empty list — in the merchant's
// language. App.jsx hands GRID_LOCALE_OPTIONS to provideGlobalGridOptions, so
// every grid gets it without asking.
//
// AG Grid asks for every word a grid could show, the unshown ones included.
// Each key it asks for is either a word we translate (AG_GRID_WORDS) or one no
// grid shows (isUnshownWord), which keeps AG Grid's English. Any other key is a
// word with no translation: it shows in English and is logged once, so a new
// one is seen and added to one of the two lists.
//
// AG Grid reads a word when it builds the part that shows it, so after a
// language switch a part already on screen keeps its old words until the grid
// is built again. Numbers in the pagination bar come from useGridPagination.

// The i18next instance i18n/i18n.jsx sets up at app start: AG Grid asks outside
// React, so it reads the language current at that time.
import i18next from 'i18next';

const AG_GRID_WORDS = {
  // Pagination bar
  to: () => i18next.t('grid.to'),
  of: () => i18next.t('grid.of'),
  page: () => i18next.t('grid.page'),
  number: () => i18next.t('grid.number'),
  more: () => i18next.t('grid.more'),
  firstPage: () => i18next.t('grid.firstPage'),
  previousPage: () => i18next.t('grid.previousPage'),
  nextPage: () => i18next.t('grid.nextPage'),
  lastPage: () => i18next.t('grid.lastPage'),
  // Columns panel (gridDefaults COLUMN_PICKER_SIDEBAR)
  columns: () => i18next.t('grid.columns'),
  searchOoo: () => i18next.t('grid.search'),
  // Right-click menu (gridDefaults copyOnlyContextMenuItems)
  copy: () => i18next.t('grid.copy'),
  copyWithHeaders: () => i18next.t('grid.copyWithHeaders'),
  // Dropdown cell editor (agRichSelectCellEditor)
  loadingOoo: () => i18next.t('common.loading'),
  noMatches: () => i18next.t('grid.noMatches'),
};

const UNSHOWN_WORDS = new Set([
  // The Columns panel's row-group and pivot zones (COLUMN_PICKER_SIDEBAR suppresses both).
  'groups', 'pivots', 'rowGroupColumnsEmptyMessage', 'pivotColumnsEmptyMessage',
  // The page size picker (useGridPagination turns it off).
  'pageSizeSelectorLabel',
  // The auto group column's header: every grouped grid names its own.
  'group',
  // Number separators: the pagination bar formats through useGridPagination.
  'thousandSeparator', 'decimalSeparator',
  // The last row of a page whose count is not known yet: '?' in every language.
  'pageLastRowUnknown',
]);

// Advanced filter operators (no grid enables the advanced filter) and
// screen-reader labels.
function isUnshownWord(key) {
  return key.startsWith('advancedFilter') || key.startsWith('aria') || UNSHOWN_WORDS.has(key);
}

const reported = new Set();

export function getGridLocaleText({ key, defaultValue }) {
  const word = AG_GRID_WORDS[key];
  if (word) return word();
  if (!isUnshownWord(key) && !reported.has(key)) {
    reported.add(key);
    console.error('gridLocale: AG Grid word "%s" has no translation and shows in English: "%s"', key, defaultValue);
  }
  return defaultValue;
}

export const GRID_LOCALE_OPTIONS = {
  getLocaleText: getGridLocaleText,
};
